import type { MarketplaceOffer } from "@/data/contractor/marketplaceOffer";

type ContractorLike = {
  id: string;
  companyName: string;
};

function normalizeCompanyName(value?: string | null): string {
  return (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

export function isExpiredPendingOffer(
  offer: Pick<MarketplaceOffer, "status" | "expiresAt">,
  nowMs: number = Date.now(),
): boolean {
  if (offer.status !== "pending") return false;
  const expiresAtMs = Date.parse(String(offer.expiresAt ?? ""));
  return Number.isFinite(expiresAtMs) && expiresAtMs <= nowMs;
}

export function isTerminalOffer(
  offer: Pick<MarketplaceOffer, "status" | "expiresAt">,
  nowMs: number = Date.now(),
): boolean {
  return (
    offer.status === "declined" ||
    offer.status === "expired" ||
    isExpiredPendingOffer(offer, nowMs)
  );
}

export function resolveOfferWorkspaceState(
  offers: MarketplaceOffer[],
  nowMs: number = Date.now(),
): {
  activeOffer: MarketplaceOffer | null;
  acceptedOffer: MarketplaceOffer | null;
  activePendingOffer: MarketplaceOffer | null;
  terminalLatestOffer: MarketplaceOffer | null;
  terminalReofferFlow: boolean;
} {
  const acceptedOffer = offers.find((offer) => offer.status === "accepted") ?? null;
  const activePendingOffer =
    offers.find(
      (offer) => offer.status === "pending" && !isExpiredPendingOffer(offer, nowMs),
    ) ?? null;
  const latestOffer = offers[0] ?? null;
  const terminalLatestOffer =
    !activePendingOffer && latestOffer && isTerminalOffer(latestOffer, nowMs)
      ? latestOffer
      : null;
  const activeOffer = activePendingOffer ?? (terminalLatestOffer ? null : acceptedOffer);

  return {
    activeOffer,
    acceptedOffer,
    activePendingOffer,
    terminalLatestOffer,
    terminalReofferFlow: Boolean(terminalLatestOffer),
  };
}

export function shouldBlockOfferRecipientChange(args: {
  selectedContractor?: ContractorLike | null;
  assignedContractorId?: string | null;
  assignedContractorName?: string | null;
  terminalReofferFlow?: boolean;
  acceptedReassignmentFlow?: boolean;
}): boolean {
  const {
    selectedContractor,
    assignedContractorId,
    assignedContractorName,
    terminalReofferFlow,
    acceptedReassignmentFlow,
  } = args;
  if (!selectedContractor) return false;
  if (terminalReofferFlow || acceptedReassignmentFlow) return false;

  const selectedNameNorm = normalizeCompanyName(selectedContractor.companyName);
  const assignedNameNorm = normalizeCompanyName(assignedContractorName);

  if (assignedContractorId) {
    if (selectedContractor.id === assignedContractorId) return false;
    if (assignedNameNorm.length > 0 && selectedNameNorm === assignedNameNorm) return false;
    return true;
  }

  if (assignedNameNorm.length > 0 && selectedNameNorm !== assignedNameNorm) return true;
  return false;
}

export function getPreferredOfferRecipientId(args: {
  selectedContractorId?: string | null;
  terminalLatestOfferContractorId?: string | null;
  acceptedOfferContractorId?: string | null;
  assignedContractorId?: string | null;
  assignedContractorName?: string | null;
  eligibleContractors: ContractorLike[];
  preferDifferentFromAccepted?: boolean;
}): string | null {
  const {
    selectedContractorId,
    terminalLatestOfferContractorId,
    acceptedOfferContractorId,
    assignedContractorId,
    assignedContractorName,
    eligibleContractors,
    preferDifferentFromAccepted,
  } = args;

  const assignedNameNorm = normalizeCompanyName(assignedContractorName);
  const acceptedAlternative =
    preferDifferentFromAccepted && acceptedOfferContractorId
      ? eligibleContractors.find((contractor) => contractor.id !== acceptedOfferContractorId)?.id ?? null
      : null;

  const candidates = [
    selectedContractorId ?? null,
    acceptedAlternative,
    terminalLatestOfferContractorId ?? null,
    assignedContractorId ?? null,
    assignedNameNorm
      ? eligibleContractors.find(
          (contractor) => normalizeCompanyName(contractor.companyName) === assignedNameNorm,
        )?.id ?? null
      : null,
    eligibleContractors[0]?.id ?? null,
  ];

  for (const candidate of candidates) {
    if (candidate && eligibleContractors.some((contractor) => contractor.id === candidate)) {
      return candidate;
    }
  }

  return null;
}
